import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  CircleNotchIcon,
  LightningIcon,
  WarningCircleIcon,
  PlusIcon,
  CheckIcon,
  MinusIcon,
  ArrowRightIcon,
  TrashIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import type {
  SupplierPayment,
  RebateRuleWithId,
  CreateAllocationRequest,
  PaymentAllocation,
} from '@/api/rebates/types';
import { RebateSupplierPaymentAllocationType } from '@/api/rebates/types';
import {
  usePaymentAllocations,
  useReplaceAllocations,
  useAllocationsByAgreement,
} from '@/api/rebates/usePaymentAllocations';
import { useToast } from '@/context/ToastContext';
import useDivisions from '@/api/organization/useDivisions';
import { EditableAllocationBreakdownTable } from './EditableAllocationBreakdownTable';
import type { CalculatedBreakdown } from '@/api/rebates/types';

import { formatCurrency } from '@erp/shared-utils';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { sortDatesChronologically } from '@/utils/breakdown-transforms/timeFilter';
import {
  allocateAuto,
  allocateByMonth,
  allocateByDivision,
  allocateByRule,
  type AllocationState,
} from '@/utils/allocationUtils';

interface PaymentAllocationModalProps {
  open: boolean;
  onClose: () => void;
  payment: SupplierPayment | null;
  rules: RebateRuleWithId[];
  onSuccess?: () => void;
  calculatedBreakdowns: CalculatedBreakdown[]; // Required: extracted from forecast data in parent
  forecastDates?: Date[]; // Optional: all dates from forecast (including months with zero values)
}

export function PaymentAllocationModal({
  open,
  onClose,
  payment,
  rules,
  onSuccess,
  calculatedBreakdowns: providedCalculatedBreakdowns,
  forecastDates,
}: PaymentAllocationModalProps) {
  const [allocations, setAllocations] = useState<AllocationState>({});
  const [selectedRulesForAllocation, setSelectedRulesForAllocation] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedDivisionsForAllocation, setSelectedDivisionsForAllocation] =
    useState<MultiSelectOption[]>([]);
  const [selectedDates, setSelectedDates] = useState<MultiSelectOption[]>([]);
  const [distributeLeftovers, setDistributeLeftovers] = useState(false);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const { data: divisionsData } = useDivisions();
  const divisions = useMemo(
    () => divisionsData?.divisions || [],
    [divisionsData],
  );

  const agreementId = payment?.rebateAgreementId || null;
  const { data: existingAllocations } = usePaymentAllocations(
    payment?.id || null,
  );
  // Use calculated breakdowns from parent (extracted from forecast data)
  const calculatedBreakdowns = providedCalculatedBreakdowns;
  const { data: globalAllocations = [] } =
    useAllocationsByAgreement(agreementId);
  const replaceAllocationsMutation = useReplaceAllocations(payment?.id || '');
  const { showToast } = useToast();

  // Get unique dates - use forecastDates if provided (includes all months),
  // otherwise fall back to dates from calculatedBreakdowns
  const availableDates = useMemo(() => {
    if (forecastDates && forecastDates.length > 0) {
      // Extract all dates from forecast (includes months with zero values)
      const dateStrings = forecastDates.map((date) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      });
      return sortDatesChronologically(dateStrings);
    }
    // Fallback: extract dates from calculated breakdowns
    const uniqueDates = new Set<string>();
    calculatedBreakdowns.forEach((bd) => {
      uniqueDates.add(bd.date);
    });
    return sortDatesChronologically(Array.from(uniqueDates));
  }, [forecastDates, calculatedBreakdowns]);

  // Use selected dates, or default to all available dates
  const dates = useMemo(() => {
    if (selectedDates.length === 0) {
      return availableDates;
    }
    return sortDatesChronologically(selectedDates.map((d) => d.id));
  }, [selectedDates, availableDates]);

  // Prepare date options for MultiSelect
  const dateOptions = useMemo<MultiSelectOption[]>(() => {
    return availableDates.map((date) => {
      const dateObj = new Date(date);
      const monthName = dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return {
        id: date,
        label: monthName,
      };
    });
  }, [availableDates]);

  const hasExistingAllocations = useMemo(
    () => existingAllocations && existingAllocations.length > 0,
    [existingAllocations],
  );

  const totalPaymentAmount = useMemo(
    () => payment?.amount || 0,
    [payment?.amount],
  );

  const unallocatedAmount = useMemo(
    () => payment?.unallocatedAmount || totalPaymentAmount,
    [payment?.unallocatedAmount, totalPaymentAmount],
  );

  // When editing existing allocations, work with total payment amount
  // When creating new allocations, work with unallocated amount
  const availableAmount = hasExistingAllocations
    ? totalPaymentAmount
    : unallocatedAmount;

  // Calculate total allocated from form (sum of all allocation values)
  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, amount) => sum + amount, 0),
    [allocations],
  );

  // Calculate remaining amount (computed on-demand)
  const remaining = useMemo(
    () => availableAmount - totalAllocated,
    [availableAmount, totalAllocated],
  );

  // Initialize allocations from existing allocations
  useEffect(() => {
    if (open && payment && existingAllocations) {
      const initialState: AllocationState = {};
      existingAllocations.forEach((alloc) => {
        const key = `${alloc.rebateRuleId}|${alloc.divisionId || 'null'}|${alloc.date}`;
        initialState[key] = alloc.allocatedAmount;
      });
      setAllocations(initialState);
    } else if (open && payment && !existingAllocations) {
      // No existing allocations, start fresh
      setAllocations({});
    }
  }, [open, payment, existingAllocations]);

  // Reset selected rules, divisions, dates, distributeLeftovers, and preview state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedRulesForAllocation([]);
      setSelectedDivisionsForAllocation([]);
      setSelectedDates([]);
      setDistributeLeftovers(false);
      setIsPreviewExpanded(false);
    }
  }, [open]);

  // Initialize selected dates to all available dates when modal opens
  useEffect(() => {
    if (open && availableDates.length > 0 && selectedDates.length === 0) {
      setSelectedDates(dateOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, availableDates.length]);

  // Prepare rule options for MultiSelect - only show rules that have calculated breakdowns
  // Filter by selected dates if any are selected, otherwise use all available dates
  const ruleOptions = useMemo<MultiSelectOption[]>(() => {
    // Get dates to filter by (selected dates or all available dates)
    const datesToFilter =
      selectedDates.length > 0
        ? selectedDates.map((d) => d.id)
        : availableDates;
    const dateSet = new Set(datesToFilter);

    // Get unique rule IDs from calculated breakdowns (only those with rebate amounts in selected dates)
    const rulesWithBreakdowns = new Set<string>();
    calculatedBreakdowns.forEach((bd) => {
      if (bd.rebateAmount > 0 && dateSet.has(bd.date)) {
        rulesWithBreakdowns.add(bd.ruleId);
      }
    });

    // Filter rules to only include those with breakdowns
    return rules
      .filter((rule) => rulesWithBreakdowns.has(rule.id))
      .map((rule) => ({
        id: rule.id,
        label: rule.name,
      }));
  }, [rules, calculatedBreakdowns, selectedDates, availableDates]);

  // Prepare division options for MultiSelect - only show divisions that have calculated breakdowns
  // Filter by selected dates if any are selected, otherwise use all available dates
  const divisionOptions = useMemo<MultiSelectOption[]>(() => {
    // Get dates to filter by (selected dates or all available dates)
    const datesToFilter =
      selectedDates.length > 0
        ? selectedDates.map((d) => d.id)
        : availableDates;
    const dateSet = new Set(datesToFilter);

    // Get unique division IDs from calculated breakdowns (only those with rebate amounts in selected dates)
    const divisionsWithBreakdowns = new Set<string>();
    calculatedBreakdowns.forEach((bd) => {
      if (bd.rebateAmount > 0 && dateSet.has(bd.date)) {
        divisionsWithBreakdowns.add(bd.divisionId);
      }
    });

    // Filter divisions to only include those with breakdowns
    return divisions
      .filter((division) => divisionsWithBreakdowns.has(division.id))
      .map((division) => ({
        id: division.id,
        label: division.name,
      }));
  }, [divisions, calculatedBreakdowns, selectedDates, availableDates]);

  // Handler for allocation changes from the breakdown table
  const handleAllocationChange = useCallback(
    (ruleId: string, divisionId: string, date: string, amount: number) => {
      const key = `${ruleId}|${divisionId}|${date}`;
      setAllocations((prev) => {
        const updated = { ...prev };
        if (amount > 0) {
          updated[key] = amount;
        } else {
          delete updated[key];
        }
        return updated;
      });
    },
    [],
  );

  // Compare existing allocations with current form state
  const allocationComparison = useMemo(() => {
    if (!hasExistingAllocations || !existingAllocations) {
      return [];
    }

    type ComparisonItem = {
      allocationId: string;
      ruleId: string;
      ruleName: string;
      divisionId: string | null;
      divisionName: string | null;
      date: string;
      oldAmount: number;
      newAmount: number;
      status: 'unchanged' | 'modified' | 'removed' | 'added';
    };

    const items: ComparisonItem[] = [];

    // Build a map of new allocations by their original allocation ID
    const newAllocMap = new Map<
      string,
      {
        amount: number;
        ruleId: string;
        divisionId: string | null;
        date: string;
      }
    >();
    Object.entries(allocations).forEach(([key, amount]) => {
      if (amount > 0) {
        const [ruleId, divisionIdStr, date] = key.split('|');
        const divisionId = divisionIdStr === 'null' ? null : divisionIdStr;
        // Find the corresponding existing allocation for this rule+division+date combination
        const existingAlloc = existingAllocations.find(
          (alloc) =>
            alloc.rebateRule.id === ruleId &&
            alloc.divisionId === divisionId &&
            alloc.date === date,
        );
        const allocationId = existingAlloc?.id || `new-${key}`;
        newAllocMap.set(allocationId, {
          amount,
          ruleId,
          divisionId,
          date,
        });
      }
    });

    // Process existing allocations
    existingAllocations.forEach((alloc: PaymentAllocation) => {
      const key = `${alloc.rebateRule.id}|${alloc.divisionId || 'null'}|${alloc.date}`;
      const newAmount = allocations[key] || 0;
      const oldAmount = alloc.allocatedAmount;

      let status: ComparisonItem['status'];
      if (newAmount === 0) {
        status = 'removed';
      } else if (Math.abs(newAmount - oldAmount) < 0.01) {
        status = 'unchanged';
      } else {
        status = 'modified';
      }

      items.push({
        allocationId: alloc.id,
        ruleId: alloc.rebateRule.id,
        ruleName: alloc.rebateRule.name,
        divisionId: alloc.divisionId,
        divisionName: alloc.division?.name || null,
        date: alloc.date,
        oldAmount,
        newAmount,
        status,
      });

      // Mark this as processed
      newAllocMap.delete(alloc.id);
    });

    // Process any new allocations that didn't exist before
    newAllocMap.forEach(
      ({ amount, ruleId, divisionId, date }, allocationId) => {
        const rule = rules.find((r) => r.id === ruleId);
        const division = divisionId
          ? divisions.find((d) => d.id === divisionId)
          : null;

        items.push({
          allocationId,
          ruleId,
          ruleName: rule?.name || 'Unknown Rule',
          divisionId,
          divisionName: division?.name || null,
          date,
          oldAmount: 0,
          newAmount: amount,
          status: 'added',
        });
      },
    );

    return items.sort((a, b) => {
      // Sort by rule order first
      const aRuleIndex = rules.findIndex((r) => r.id === a.ruleId);
      const bRuleIndex = rules.findIndex((r) => r.id === b.ruleId);

      if (aRuleIndex !== bRuleIndex) {
        return aRuleIndex - bRuleIndex;
      }

      // If same rule, sort by date, then division name
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }

      if (a.divisionName === null && b.divisionName === null) return 0;
      if (a.divisionName === null) return 1;
      if (b.divisionName === null) return -1;
      return a.divisionName.localeCompare(b.divisionName);
    });
  }, [
    hasExistingAllocations,
    existingAllocations,
    allocations,
    rules,
    divisions,
  ]);

  function validateAndSubmit() {
    // Build allocation requests from the new state format
    const allocationRequests: CreateAllocationRequest[] = [];

    Object.entries(allocations).forEach(([key, amount]) => {
      if (amount <= 0) return; // Skip zero allocations

      const [ruleId, divisionIdStr, date] = key.split('|');
      const divisionId = divisionIdStr === 'null' ? null : divisionIdStr;

      allocationRequests.push({
        rebateRuleId: ruleId,
        divisionId,
        date,
        allocatedAmount: parseFloat(amount.toFixed(2)),
        allocationType: RebateSupplierPaymentAllocationType.MANUAL,
      });
    });

    handleSubmit(allocationRequests);
  }

  // Auto-allocate: distribute payment proportionally based on expected rebate amounts
  // Accounts for existing allocations and caps each allocation at remaining capacity
  // Respects the selected date range (dates array)
  function handleAutoAllocate() {
    if (!payment || calculatedBreakdowns.length === 0) {
      showToast({
        message: 'No calculated breakdowns available for auto-allocation',
        title: 'Cannot Auto-allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (dates.length === 0) {
      showToast({
        message: 'Please select at least one date to allocate',
        title: 'No Dates Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    // Reset all allocations first
    setAllocations({});

    const newAllocations = allocateAuto({
      calculatedBreakdowns,
      dates,
      availableAmount,
      globalAllocations,
      paymentId: payment.id,
    });

    if (Object.keys(newAllocations).length === 0) {
      showToast({
        message:
          'No breakdowns available for the selected date range or all expected rebates have already been fully allocated',
        title: 'Cannot Auto-allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    setAllocations(newAllocations);
  }

  // Allocate by Month: waterfall chronologically through months
  // Fully allocates each month before moving to the next
  function handleAllocateByMonth() {
    if (!payment || calculatedBreakdowns.length === 0) {
      showToast({
        message: 'No calculated breakdowns available',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (dates.length === 0) {
      showToast({
        message: 'Please select at least one date to allocate',
        title: 'No Dates Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    // Reset all allocations first
    setAllocations({});

    const newAllocations = allocateByMonth({
      calculatedBreakdowns,
      dates,
      availableAmount,
      globalAllocations,
      paymentId: payment.id,
    });

    if (Object.keys(newAllocations).length === 0) {
      showToast({
        message: 'No breakdowns available for the selected date range',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    setAllocations(newAllocations);
  }

  // Allocate by Division: user selects divisions, then waterfall chronologically through dates
  function handleAllocateByDivision() {
    if (!payment || calculatedBreakdowns.length === 0) {
      showToast({
        message: 'No calculated breakdowns available',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (dates.length === 0) {
      showToast({
        message: 'Please select at least one date to allocate',
        title: 'No Dates Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (selectedDivisionsForAllocation.length === 0) {
      showToast({
        message: 'Please select at least one division to allocate',
        title: 'No Divisions Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    // Reset all allocations first
    setAllocations({});

    const newAllocations = allocateByDivision({
      calculatedBreakdowns,
      dates,
      availableAmount,
      globalAllocations,
      paymentId: payment.id,
      selectedDivisionIds: selectedDivisionsForAllocation.map((d) => d.id),
    });

    if (Object.keys(newAllocations).length === 0) {
      showToast({
        message:
          'No breakdowns available for selected divisions and date range',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    setAllocations(newAllocations);
  }

  // Allocate by Rule: user selects rules, then waterfall chronologically through dates
  function handleAllocateByRule() {
    if (!payment || calculatedBreakdowns.length === 0) {
      showToast({
        message: 'No calculated breakdowns available',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (dates.length === 0) {
      showToast({
        message: 'Please select at least one date to allocate',
        title: 'No Dates Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    if (selectedRulesForAllocation.length === 0) {
      showToast({
        message: 'Please select at least one rule to allocate',
        title: 'No Rules Selected',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    // Reset all allocations first
    setAllocations({});

    const newAllocations = allocateByRule({
      calculatedBreakdowns,
      dates,
      availableAmount,
      globalAllocations,
      paymentId: payment.id,
      selectedRuleIds: selectedRulesForAllocation.map((r) => r.id),
    });

    if (Object.keys(newAllocations).length === 0) {
      showToast({
        message: 'No breakdowns available for selected rules and date range',
        title: 'Cannot Allocate',
        color: 'error',
        icon: 'warning',
      });
      return;
    }

    setAllocations(newAllocations);
  }

  function clearAllAllocations() {
    setAllocations({});
  }

  async function handleSubmit(allocationRequests: CreateAllocationRequest[]) {
    if (!payment) return;

    try {
      // Always use replace - if no existing allocations, it just creates new ones
      await replaceAllocationsMutation.mutateAsync({
        existingAllocationIds: existingAllocations?.map((a) => a.id) || [],
        newAllocations: { allocations: allocationRequests },
      });

      showToast({
        message: hasExistingAllocations
          ? `Successfully updated allocations: ${formatCurrency(totalAllocated)}`
          : `Successfully allocated ${formatCurrency(totalAllocated)}`,
        title: 'Success',
        color: 'success',
        icon: 'check',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving allocations:', error);
      showToast({
        message: hasExistingAllocations
          ? 'Failed to update allocations'
          : 'Failed to create allocations',
        title: 'Error',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  if (!payment) return null;

  const isOverAllocated = remaining < 0;
  const isUnderAllocated = remaining > 0.01; // Tolerance for floating point

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="!max-w-[95vw] sm:!max-w-[95vw] !w-[95vw]  flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {hasExistingAllocations ? 'Update' : 'Allocate'} Payment
          </DialogTitle>
          <DialogDescription>
            {hasExistingAllocations ? (
              <span>
                This payment has existing allocations. Your changes will replace
                all existing allocations.
              </span>
            ) : (
              <span>
                Distribute this {formatCurrency(totalPaymentAmount)} payment
                across rebate rules and divisions
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Payment Summary */}
          <Card className="p-4 bg-muted/50">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Payment Amount</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(totalPaymentAmount)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Currently Allocated</div>
                <div className="text-lg font-semibold text-blue-600">
                  {formatCurrency(totalAllocated)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">
                  {remaining >= 0
                    ? 'Remaining Unallocated'
                    : remaining < 0
                      ? 'Over-allocated'
                      : 'Allocated'}
                </div>
                <div
                  className={`text-lg font-semibold ${remaining > 0 ? 'text-yellow-600' : remaining === 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {formatCurrency(Math.abs(remaining))}
                </div>
              </div>
            </div>
          </Card>

          {/* Allocation Interface */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">
                Allocate across rules, divisions, and time
              </h3>
              <TooltipProvider>
                <div className="flex items-center gap-2 flex-wrap">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearAllAllocations}
                        className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Clear All
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Clear all allocation amounts</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAutoAllocate}
                        className="gap-2"
                      >
                        <LightningIcon className="h-4 w-4" />
                        Auto-allocate
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Distribute payment proportionally across all selected
                        dates, rules, and divisions based on expected rebate
                        amounts. All dates receive a proportional share
                        simultaneously.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAllocateByMonth}
                        className="gap-2"
                      >
                        <LightningIcon className="h-4 w-4" />
                        Allocate by Month
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Waterfall allocation: fully allocate each month
                        chronologically before moving to the next. Earlier
                        months are prioritized and fully allocated first.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAllocateByRule}
                        className="gap-2"
                        disabled={selectedRulesForAllocation.length === 0}
                      >
                        <LightningIcon className="h-4 w-4" />
                        Allocate by Rule
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        For each selected rule, waterfall chronologically
                        through dates. Fully allocate each date for the rule
                        before moving to the next date. Select rules above to
                        use this option.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAllocateByDivision}
                        className="gap-2"
                        disabled={selectedDivisionsForAllocation.length === 0}
                      >
                        <LightningIcon className="h-4 w-4" />
                        Allocate by Division
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        For each selected division, waterfall chronologically
                        through dates. Fully allocate each date for the division
                        before moving to the next date. Select divisions above
                        to use this option.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>

            {/* Date Range, Rule, and Division Selection */}
            <div className="mb-4 flex items-start gap-4">
              <MultiSelect
                options={dateOptions}
                value={selectedDates}
                onChange={setSelectedDates}
                placeholder="Select dates to include..."
                label="Date Range"
                maxChips={4}
                size="sm"
                shouldAutoSort={false}
                hideChips={true}
              />
              <MultiSelect
                options={ruleOptions}
                value={selectedRulesForAllocation}
                onChange={setSelectedRulesForAllocation}
                placeholder="Select rules for allocation..."
                label="Rules (for Allocate by Rule)"
                maxChips={3}
                size="sm"
                hideChips={true}
              />
              <MultiSelect
                options={divisionOptions}
                value={selectedDivisionsForAllocation}
                onChange={setSelectedDivisionsForAllocation}
                placeholder="Select divisions for allocation..."
                label="Divisions (for Allocate by Division)"
                maxChips={3}
                hideChips={true}
                size="sm"
              />
            </div>

            {calculatedBreakdowns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No calculated breakdowns available for this agreement.</p>
                <p className="text-sm mt-2">
                  Please generate forecasts to see expected rebate amounts.
                </p>
              </div>
            ) : dates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No dates with non-zero estimates found.</p>
              </div>
            ) : (
              <EditableAllocationBreakdownTable
                rules={rules.map((r) => ({ id: r.id, name: r.name }))}
                divisions={divisions}
                dates={dates}
                calculatedBreakdowns={calculatedBreakdowns}
                globalAllocations={globalAllocations}
                currentAllocations={allocations}
                onAllocationChange={handleAllocationChange}
                currentPaymentId={payment?.id}
              />
            )}

            {/* Allocation Changes Preview */}
            {hasExistingAllocations && allocationComparison.length > 0 && (
              <Card className="p-4 border-slate-200 bg-slate-50 mt-4">
                <div className="flex items-start gap-3">
                  <WarningCircleIcon className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={() => setIsPreviewExpanded(!isPreviewExpanded)}
                      className="flex items-center justify-between w-full text-sm font-semibold text-slate-900 mb-3 hover:text-slate-700 transition-colors"
                    >
                      <span>Allocation Changes Preview</span>
                      {isPreviewExpanded ? (
                        <CaretUpIcon className="h-4 w-4" />
                      ) : (
                        <CaretDownIcon className="h-4 w-4" />
                      )}
                    </button>
                    {isPreviewExpanded && (
                      <div className="space-y-1.5 text-sm">
                        {allocationComparison.map((item, idx) => {
                          const statusConfig = {
                            unchanged: {
                              bg: 'bg-green-50',
                              border: 'border-green-200',
                              text: 'text-green-700',
                              icon: <CheckIcon className="h-3.5 w-3.5" />,
                              iconBg: 'bg-green-100',
                              label: 'No change',
                            },
                            modified: {
                              bg: 'bg-blue-50',
                              border: 'border-blue-200',
                              text: 'text-blue-700',
                              icon: <ArrowRightIcon className="h-3.5 w-3.5" />,
                              iconBg: 'bg-blue-100',
                              label: 'Modified',
                            },
                            removed: {
                              bg: 'bg-slate-100',
                              border: 'border-slate-300',
                              text: 'text-slate-500',
                              icon: <MinusIcon className="h-3.5 w-3.5" />,
                              iconBg: 'bg-slate-200',
                              label: 'Removed',
                            },
                            added: {
                              bg: 'bg-yellow-50',
                              border: 'border-yellow-200',
                              text: 'text-yellow-700',
                              icon: <PlusIcon className="h-3.5 w-3.5" />,
                              iconBg: 'bg-yellow-100',
                              label: 'New',
                            },
                          };

                          const config = statusConfig[item.status];

                          return (
                            <div
                              key={idx}
                              className={`flex items-center justify-between gap-3 p-2.5 rounded-md border ${config.bg} ${config.border}`}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div
                                  className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 ${config.iconBg} ${config.text}`}
                                >
                                  {config.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className={`font-medium ${config.text}`}>
                                    {item.ruleName}
                                    {item.divisionName && (
                                      <span className="ml-2 text-xs opacity-75">
                                        → {item.divisionName}
                                      </span>
                                    )}
                                    <span className="ml-2 text-xs opacity-75">
                                      (
                                      {new Date(item.date).toLocaleDateString(
                                        'en-US',
                                        { month: 'short', year: 'numeric' },
                                      )}
                                      )
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div
                                className={`flex items-center gap-2 tabular-nums text-sm font-semibold flex-shrink-0 ${config.text}`}
                              >
                                {item.status === 'modified' ? (
                                  <>
                                    <span className="line-through opacity-60">
                                      {formatCurrency(item.oldAmount)}
                                    </span>
                                    <ArrowRightIcon className="h-3 w-3" />
                                    <span>
                                      {formatCurrency(item.newAmount)}
                                    </span>
                                  </>
                                ) : item.status === 'removed' ? (
                                  <span className="line-through">
                                    {formatCurrency(item.oldAmount)}
                                  </span>
                                ) : item.status === 'added' ? (
                                  <span>{formatCurrency(item.newAmount)}</span>
                                ) : (
                                  <span>{formatCurrency(item.oldAmount)}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </Card>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={replaceAllocationsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={validateAndSubmit}
            disabled={replaceAllocationsMutation.isPending}
          >
            {replaceAllocationsMutation.isPending ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : hasExistingAllocations ? (
              `Update`
            ) : (
              `Allocate`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
